"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ColorRectangleBase = void 0;
exports._getNewColor = _getNewColor;
var tslib_1 = require("tslib");
var React = require("react");
var Utilities_1 = require("../../../Utilities");
// These imports are separated to help with bundling
var consts_1 = require("../../../utilities/color/consts");
var getFullColorString_1 = require("../../../utilities/color/getFullColorString");
var updateSV_1 = require("../../../utilities/color/updateSV");
var clamp_1 = require("../../../utilities/color/clamp");
var react_window_provider_1 = require("@fluentui/react-window-provider");
var dom_1 = require("../../../utilities/dom");
var getClassNames = (0, Utilities_1.classNamesFunction)();
/**
 * {@docCategory ColorPicker}
 */
var ColorRectangleBase = /** @class */ (function (_super) {
    tslib_1.__extends(ColorRectangleBase, _super);
    function ColorRectangleBase(props) {
        var _this = _super.call(this, props) || this;
        _this._disposables = [];
        _this._root = React.createRef();
        _this._isAdjustingSaturation = true;
        _this._descriptionId = (0, Utilities_1.getId)('ColorRectangle-description');
        _this._onKeyDown = function (ev) {
            var color = _this.state.color;
            var s = color.s, v = color.v;
            var increment = ev.shiftKey ? 10 : 1;
            // Intentionally DO NOT flip the color picker in RTL: its orientation is not very meaningful,
            // and getting all the math and styles flipped correctly is tricky
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            switch (ev.which) {
                case Utilities_1.KeyCodes.up: {
                    _this._isAdjustingSaturation = false;
                    v += increment; // V = 100 (lightest) is at the top
                    break;
                }
                case Utilities_1.KeyCodes.down: {
                    _this._isAdjustingSaturation = false;
                    v -= increment; // V = 0 (darkest) is at the bottom
                    break;
                }
                case Utilities_1.KeyCodes.left: {
                    _this._isAdjustingSaturation = true;
                    s -= increment;
                    break;
                }
                case Utilities_1.KeyCodes.right: {
                    _this._isAdjustingSaturation = true;
                    s += increment;
                    break;
                }
                default:
                    return;
            }
            _this._updateColor(ev, (0, updateSV_1.updateSV)(color, (0, clamp_1.clamp)(s, consts_1.MAX_COLOR_SATURATION), (0, clamp_1.clamp)(v, consts_1.MAX_COLOR_VALUE)));
        };
        _this._onMouseDown = function (ev) {
            var win = (0, dom_1.getWindowEx)(_this.context); // Can only be called on the client
            _this._disposables.push((0, Utilities_1.on)(win, 'mousemove', _this._onMouseMove, true), (0, Utilities_1.on)(win, 'mouseup', _this._disposeListeners, true));
            _this._onMouseMove(ev);
        };
        _this._onMouseMove = function (ev) {
            if (!_this._root.current) {
                return;
            }
            // Leaving the following commented code which is sometimes necessary for debugging:
            // If the primary button (1) isn't pressed, the user is no longer dragging, so turn off
            // the event handlers and exit.
            // if (!(ev.buttons & 1)) {
            //   this._disposeListeners();
            //   return;
            // }
            var newColor = _getNewColor(ev, _this.state.color, _this._root.current);
            if (newColor) {
                _this._updateColor(ev, newColor);
            }
        };
        _this._onTouchStart = function (ev) {
            if (!_this._root.current) {
                return;
            }
            // prevent touch from scrolling the page so that the touch can be dragged on the color rectangle.
            ev.stopPropagation();
        };
        _this._onTouchMove = function (ev) {
            if (!_this._root.current) {
                return;
            }
            var newColor = _getNewColor(ev, _this.state.color, _this._root.current);
            if (newColor) {
                _this._updateColor(ev, newColor);
            }
            ev.preventDefault();
            ev.stopPropagation();
        };
        _this._disposeListeners = function () {
            _this._disposables.forEach(function (dispose) { return dispose(); });
            _this._disposables = [];
        };
        (0, Utilities_1.initializeComponentRef)(_this);
        _this.state = { color: props.color };
        return _this;
    }
    Object.defineProperty(ColorRectangleBase.prototype, "color", {
        get: function () {
            return this.state.color;
        },
        enumerable: false,
        configurable: true
    });
    ColorRectangleBase.prototype.componentDidUpdate = function (prevProps, prevState) {
        // if props changed (as opposed to a state update), set the value
        // TODO: switch to strict controlled pattern instead
        if (prevProps !== this.props && this.props.color) {
            this.setState({ color: this.props.color });
        }
    };
    ColorRectangleBase.prototype.componentDidMount = function () {
        if (this._root.current) {
            // with Chrome's passive DOM listeners, stopPropagation and preventDefault only work if passive is false.
            this._root.current.addEventListener('touchstart', this._onTouchStart, { capture: true, passive: false });
            this._root.current.addEventListener('touchmove', this._onTouchMove, { capture: true, passive: false });
        }
    };
    ColorRectangleBase.prototype.componentWillUnmount = function () {
        if (this._root.current) {
            this._root.current.removeEventListener('touchstart', this._onTouchStart);
            this._root.current.removeEventListener('touchmove', this._onTouchMove);
        }
        this._disposeListeners();
    };
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    ColorRectangleBase.prototype.render = function () {
        var _a = this.props, minSize = _a.minSize, theme = _a.theme, className = _a.className, styles = _a.styles, ariaValueFormat = _a.ariaValueFormat, ariaLabel = _a.ariaLabel, ariaDescription = _a.ariaDescription;
        var color = this.state.color;
        var classNames = getClassNames(styles, {
            theme: theme,
            className: className,
            minSize: minSize,
        });
        var valueText = ariaValueFormat.replace('{0}', String(color.s)).replace('{1}', String(color.v));
        return (React.createElement("div", { ref: this._root, tabIndex: 0, className: classNames.root, style: { backgroundColor: (0, getFullColorString_1.getFullColorString)(color) }, onMouseDown: this._onMouseDown, onKeyDown: this._onKeyDown, role: "slider", "aria-valuetext": valueText, "aria-valuenow": this._isAdjustingSaturation ? color.s : color.v, "aria-valuemin": 0, "aria-valuemax": consts_1.MAX_COLOR_VALUE, "aria-label": ariaLabel, "aria-describedby": this._descriptionId, "data-is-focusable": true },
            React.createElement("div", { className: classNames.description, id: this._descriptionId }, ariaDescription),
            React.createElement("div", { className: classNames.light }),
            React.createElement("div", { className: classNames.dark }),
            React.createElement("div", { className: classNames.thumb, style: { left: color.s + '%', top: consts_1.MAX_COLOR_VALUE - color.v + '%', backgroundColor: color.str } })));
    };
    ColorRectangleBase.prototype._updateColor = function (ev, color) {
        var onChange = this.props.onChange;
        var oldColor = this.state.color;
        if (color.s === oldColor.s && color.v === oldColor.v) {
            return; // no change
        }
        if (onChange) {
            onChange(ev, color);
        }
        if (!ev.defaultPrevented) {
            this.setState({ color: color });
            ev.preventDefault();
        }
    };
    ColorRectangleBase.defaultProps = {
        minSize: 220,
        ariaLabel: 'Saturation and brightness',
        ariaValueFormat: 'Saturation {0} brightness {1}',
        ariaDescription: 'Use left and right arrow keys to set saturation. Use up and down arrow keys to set brightness.',
    };
    ColorRectangleBase.contextType = react_window_provider_1.WindowContext;
    return ColorRectangleBase;
}(React.Component));
exports.ColorRectangleBase = ColorRectangleBase;
/**
 * Exported for testing only.
 * @internal
 */
function _getNewColor(ev, prevColor, root) {
    var rectSize = root.getBoundingClientRect();
    var coords = undefined;
    var touchEvent = ev;
    if (touchEvent.touches) {
        var lastTouch = touchEvent.touches[touchEvent.touches.length - 1];
        if (lastTouch.clientX !== undefined && lastTouch.clientY !== undefined) {
            coords = {
                clientX: lastTouch.clientX,
                clientY: lastTouch.clientY,
            };
        }
    }
    if (!coords) {
        var mouseEvent = ev;
        if (mouseEvent.clientX !== undefined && mouseEvent.clientY !== undefined) {
            coords = {
                clientX: mouseEvent.clientX,
                clientY: mouseEvent.clientY,
            };
        }
    }
    if (coords) {
        var sPercentage = (coords.clientX - rectSize.left) / rectSize.width;
        var vPercentage = (coords.clientY - rectSize.top) / rectSize.height;
        return (0, updateSV_1.updateSV)(prevColor, (0, clamp_1.clamp)(Math.round(sPercentage * consts_1.MAX_COLOR_SATURATION), consts_1.MAX_COLOR_SATURATION), (0, clamp_1.clamp)(Math.round(consts_1.MAX_COLOR_VALUE - vPercentage * consts_1.MAX_COLOR_VALUE), consts_1.MAX_COLOR_VALUE));
    }
}
//# sourceMappingURL=ColorRectangle.base.js.map